import React, { useState, useEffect, Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import DOMPurify from 'dompurify';
import { formSchema, security } from '../../utils/security';
import type { z } from 'zod';
import { Button } from '../ui/Button';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useToast } from '../../hooks/useToast';
import { supabase } from '../../utils/supabaseClient';
import { ErrorFallback } from '../ErrorFallback';

type FormType = 'couple' | 'vendor';
type FormData = z.infer<typeof formSchema>;

const CoupleForm = React.lazy(() => import('./CoupleForm'));
const VendorForm = React.lazy(() => import('./VendorForm'));

const FORM_SUBMISSION_TIMEOUT = 10000; // 10 seconds timeout

export function WaitlistForm() {
  const [formType, setFormType] = useState<FormType>('couple');
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { trackEvent } = useAnalytics();
  const { showToast } = useToast();
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
  });

  useEffect(() => {
    const savedData = localStorage.getItem('formData');
    if (savedData) {
      try {
        setFormData(JSON.parse(savedData));
      } catch (error) {
        console.error('Error parsing saved form data:', error);
        localStorage.removeItem('formData');
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('formData', JSON.stringify(formData));
  }, [formData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const submissionPromise = new Promise(async (resolve, reject) => {
      try {
        if (security.isBlocked()) {
          throw new Error('Too many attempts. Please try again later.');
        }

        if (!security.checkRateLimit()) {
          throw new Error('Please wait before submitting again.');
        }

        const sanitizedData = {
          ...formData,
          email: DOMPurify.sanitize(formData.email.toLowerCase()),
          name: DOMPurify.sanitize(formData.name),
        };

        formSchema.parse(sanitizedData);

        const { error } = await supabase
          .from('signups')
          .insert([
            {
              name: sanitizedData.name,
              email: sanitizedData.email,
              type: formType,
              wedding_date: formType === 'couple' ? sanitizedData.weddingDate : null,
              business_name: formType === 'vendor' ? sanitizedData.businessName : null,
              service_type: formType === 'vendor' ? sanitizedData.serviceType : null,
            }
          ]);

        if (error) {
          if (error.code === '23505') {
            throw new Error('This email has already been registered.');
          }
          throw error;
        }

        security.recordAttempt();
        trackEvent('form_submitted', {
          formType,
          ...sanitizedData,
        });

        resolve(true);
      } catch (error) {
        reject(error);
      }
    });

    try {
      await Promise.race([
        submissionPromise,
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Request timed out. Please try again.')), FORM_SUBMISSION_TIMEOUT)
        )
      ]);

      showToast('Successfully signed up!', 'success');
      setSubmitted(true);
      localStorage.removeItem('formData');
    } catch (error) {
      if (error instanceof z.ZodError) {
        showToast(error.errors[0].message, 'error');
      } else if (error instanceof Error) {
        showToast(error.message, 'error');
      } else {
        showToast('An error occurred. Please try again.', 'error');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-12" role="alert">
        <h3 className="text-2xl font-bold text-gray-900 mb-4">Thank you for signing up!</h3>
        <p className="text-gray-600">We'll keep you updated on our launch.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex gap-4 mb-8 justify-center" role="tablist">
        <button
          role="tab"
          aria-selected={formType === 'couple'}
          className={`px-6 py-2 rounded-full ${
            formType === 'couple'
              ? 'bg-primary-400 text-white'
              : 'bg-white text-gray-600 border border-gray-300'
          }`}
          onClick={() => setFormType('couple')}
        >
          As a Couple
        </button>
        <button
          role="tab"
          aria-selected={formType === 'vendor'}
          className={`px-6 py-2 rounded-full ${
            formType === 'vendor'
              ? 'bg-primary-400 text-white'
              : 'bg-white text-gray-600 border border-gray-300'
          }`}
          onClick={() => setFormType('vendor')}
        >
          As a Vendor
        </button>
      </div>
      <form 
        onSubmit={handleSubmit} 
        id={formType === 'couple' ? 'couple-form' : 'vendor-form'}
        aria-label={`Sign up form for ${formType}`}
      >
        <ErrorBoundary
          FallbackComponent={ErrorFallback}
          onReset={() => {
            setFormData({
              name: '',
              email: '',
            });
          }}
        >
          <Suspense fallback={<LoadingSpinner />}>
            {formType === 'couple' ? (
              <CoupleForm formData={formData} setFormData={setFormData} />
            ) : (
              <VendorForm formData={formData} setFormData={setFormData} />
            )}
          </Suspense>
        </ErrorBoundary>

        <Button 
          type="submit" 
          className="w-full mt-6"
          disabled={isLoading}
          aria-busy={isLoading}
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <LoadingSpinner />
              Submitting...
            </span>
          ) : (
            'Join the Waitlist'
          )}
        </Button>
      </form>
    </div>
  );
}